import { useCallback, useEffect, useRef, useState } from 'react';
import { getOrderHistory } from '../../context/CommonContex';
import {
    countUnreadOrderHistory,
    getOrCreateUnreadBaseline,
    getOrderHistoryLastViewedAt,
    markOrderHistoryViewed,
} from './orderHistoryUnreadStorage';

const CONCURRENCY = 5;
const HISTORY_CHANGED_EVENT = 'order-history-changed';

/** Notify listeners that an order may have new history (comment/status/file saves). */
export const notifyOrderHistoryChanged = (ordernum) => {
    if (!ordernum || typeof window === 'undefined') return;
    // Brief delay so Spring can finish writing the history row before we re-count.
    window.setTimeout(() => {
        window.dispatchEvent(
            new CustomEvent(HISTORY_CHANGED_EVENT, { detail: { ordernum: String(ordernum) } })
        );
    }, 800);
};

const runWithConcurrency = async (items, limit, worker) => {
    const results = new Map();
    let index = 0;

    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (index < items.length) {
            const current = index;
            index += 1;
            const item = items[current];
            try {
                results.set(item, await worker(item));
            } catch {
                results.set(item, 0);
            }
        }
    });

    await Promise.all(runners);
    return results;
};

/**
 * Loads per-order unread Order History counts for the current user (localStorage).
 * @param {string[]} orderNums
 */
export const useOrderHistoryUnread = (orderNums) => {
    const [unreadByOrder, setUnreadByOrder] = useState({});
    const orderNumsKey = Array.isArray(orderNums)
        ? orderNums.map(String).filter(Boolean).sort().join('|')
        : '';
    const orderNumsRef = useRef([]);
    const abortRef = useRef(0);

    useEffect(() => {
        orderNumsRef.current = orderNumsKey ? orderNumsKey.split('|') : [];
    }, [orderNumsKey]);

    const fetchUnreadForOrder = useCallback(async (ordernum) => {
        const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');
        const entries = await getOrderHistory(ordernum);
        const lastViewed = getOrderHistoryLastViewedAt(ordernum, userEmail);
        const baseline = getOrCreateUnreadBaseline(userEmail);
        return countUnreadOrderHistory(entries, lastViewed, baseline);
    }, []);

    const refreshOrders = useCallback(async (nums) => {
        const list = (nums || []).map(String).filter(Boolean);
        if (list.length === 0) {
            setUnreadByOrder({});
            return;
        }

        const requestId = ++abortRef.current;
        const results = await runWithConcurrency(list, CONCURRENCY, fetchUnreadForOrder);
        if (requestId !== abortRef.current) return;

        setUnreadByOrder((prev) => {
            const next = { ...prev };
            results.forEach((count, ordernum) => {
                next[ordernum] = count;
            });
            // Drop counts for orders no longer in the active set
            Object.keys(next).forEach((key) => {
                if (!list.includes(key)) delete next[key];
            });
            return next;
        });
    }, [fetchUnreadForOrder]);

    const refreshUnreadForOrder = useCallback(async (ordernum) => {
        if (!ordernum) return;
        const key = String(ordernum);
        try {
            const count = await fetchUnreadForOrder(key);
            setUnreadByOrder((prev) => ({ ...prev, [key]: count }));
        } catch {
            /* keep previous */
        }
    }, [fetchUnreadForOrder]);

    const markViewed = useCallback((ordernum) => {
        if (!ordernum) return;
        const key = String(ordernum);
        const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');
        markOrderHistoryViewed(key, userEmail);
        setUnreadByOrder((prev) => ({ ...prev, [key]: 0 }));
    }, []);

    const getUnreadCount = useCallback(
        (ordernum) => {
            if (!ordernum) return 0;
            return unreadByOrder[String(ordernum)] || 0;
        },
        [unreadByOrder]
    );

    useEffect(() => {
        refreshOrders(orderNumsRef.current);
    }, [orderNumsKey, refreshOrders]);

    useEffect(() => {
        const onChanged = (event) => {
            const ordernum = event?.detail?.ordernum;
            if (ordernum) refreshUnreadForOrder(ordernum);
        };
        window.addEventListener(HISTORY_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(HISTORY_CHANGED_EVENT, onChanged);
    }, [refreshUnreadForOrder]);

    return {
        unreadByOrder,
        getUnreadCount,
        markViewed,
        refreshUnreadForOrder,
        refreshAll: () => refreshOrders(orderNumsRef.current),
    };
};
